import CardCarousel from '@Shared/components/visuals/CardCarousel.vue';
import DriverGauges from '@Shared/components/visuals/DriverGauges.vue';
import BenchmarkPrograms from '@Shared/components/visuals/BenchmarkPrograms.vue';
import FrontlinePrimaryMetric from '@Shared/components/visuals/FrontlinePrimaryMetric.vue';
import FrontlineProgramBreakdown from '@Shared/components/visuals/FrontlineProgramBreakdown.vue';
import FrontlineTopBanner from '@Shared/components/visuals/FrontlineTopBanner.vue';
import GaugeChart from '@Shared/components/visuals/GaugeChart.vue';
import MeasureLeaderboard from '@Shared/components/visuals/MeasureLeaderboard.vue';
import MeasureList from '@Shared/components/visuals/MeasureList.vue';
import ProcessFlowFeed from '@Shared/components/visuals/ProcessFlowFeed.vue';
import ProgramDriverGauges from '@Shared/components/visuals/ProgramDriverGauges.vue';
import SmartInsights from '@Shared/components/visuals/SmartInsights.vue';
import SocialReviewFeed from './SocialReviewFeed.vue';
import SocialReviewRatings from './SocialReviewRatings.vue';
import SocialReviewReplies from './SocialReviewReplies.vue';
import SocialReviewsGenerated from './SocialReviewsGenerated.vue';
import Favourites from './Favourites.vue';
import ImprovedLikeForLikeReport from './ImprovedLikeForLikeReport.vue';

import MetricBreakdownTile from '@Shared/components/visuals/MetricBreakdownTile.vue';
import OneValueTile from '@Shared/components/visuals/OneValueTile.vue';
import SlicedMetricBreakdownTiles from '@Shared/components/visuals/SlicedMetricBreakdownTiles.vue';
import TopNValuesTile from '@Shared/components/visuals/TopNValuesTile.vue';
import TopValueByMeasureTile from '@Shared/components/visuals/TopValueByMeasureTile.vue';
import MeasureTile from './MeasureTile.vue';

import SkeletonCardCarousel from '@Shared/components/visuals/skeletons/CardCarousel.vue';
import SkeletonDriverGauges from '@Shared/components/visuals/skeletons/DriverGauges.vue';
import SkeletonFrontlinePrimaryMetric from '@Shared/components/visuals/skeletons/FrontlinePrimaryMetric.vue';
import SkeletonFrontlineProgramBreakdown from '@Shared/components/visuals/skeletons/FrontlineProgramBreakdown.vue';
import SkeletonFrontlineTopBanner from '@Shared/components/visuals/skeletons/FrontlineTopBanner.vue';
import SkeletonGaugeChart from '@Shared/components/visuals/skeletons/GaugeChart.vue';
import SkeletonMeasureLeaderboard from '@Shared/components/visuals/skeletons/MeasureLeaderboard.vue';
import SkeletonMeasureList from '@Shared/components/visuals/skeletons/MeasureList.vue';
import SkeletonProcessFlowFeed from '@Shared/components/visuals/skeletons/ProcessFlowFeed.vue';
import SkeletonProgramDriverGauges from '@Shared/components/visuals/skeletons/ProgramDriverGauges.vue';
import SkeletonSmartInsights from '@Shared/components/visuals/skeletons/SmartInsights.vue';
import SkeletonTrendChart from '@Shared/components/visuals/skeletons/TrendChart.vue';

import SkeletonSocialReviewRatings from '@Shared/components/visuals/skeletons/SocialReviewRatings.vue';
import SkeletonSocialReviewReplies from '@Shared/components/visuals/skeletons/SocialReviewReplies.vue';
import SkeletonSocialReviewsGenerated from '@Shared/components/visuals/skeletons/SocialReviewsGenerated.vue';

import NpsStackedBarWithLine from '@Shared/components/CuratedReports/CrNpsStackedBarWithLineChart.vue';

import ClosingTime from '@Portal/assets/insights/icons/defaultPrompt/ClosingTime.svg?component';
import CommonTopics from '@Portal/assets/insights/icons/defaultPrompt/CommonTopics.svg?component';
import DetractorFeedbackCount from '@Portal/assets/insights/icons/defaultPrompt/DetractorFeedbackCount.svg?component';
import FeedbackTopic from '@Portal/assets/insights/icons/defaultPrompt/FeedbackTopic.svg?component';
import ImprovementPoint from '@Portal/assets/insights/icons/defaultPrompt/ImprovementPoint.svg?component';
import LowHangingDriver from '@Portal/assets/insights/icons/defaultPrompt/LowHangingDriver.svg?component';
import MetricChanged from '@Portal/assets/insights/icons/defaultPrompt/MetricChanged.svg?component';
import OpenedFeedback from '@Portal/assets/insights/icons/defaultPrompt/OpenedFeedback.svg?component';
import PerformanceSummary from '@Portal/assets/insights/icons/defaultPrompt/PerformanceSummary.svg?component';
import TopBottomPerformers from '@Portal/assets/insights/icons/defaultPrompt/TopBottomPerformers.svg?component';

// The component name has to map to the VisualType (from the backend)
// since it's what we use to look up visuals
// The skeleton is similar but has a 'Skeleton' prefix
// @FIXME: In future we should lazy load components (right now there isn't enough that it matters)
export function registerComponents(defineComponent: (componentName: string, component: any) => void) {
  defineComponent('CardCarousel', CardCarousel);
  defineComponent('DriverGauges', DriverGauges);
  defineComponent('ProgramDriverGauges', ProgramDriverGauges);
  defineComponent('BenchmarkPrograms', BenchmarkPrograms);
  defineComponent('BenchmarkProgramsDemo', BenchmarkPrograms);

  defineComponent('GaugeChart', GaugeChart);
  defineComponent('MeasureLeaderboard', MeasureLeaderboard);
  defineComponent('MeasureList', MeasureList);
  defineComponent('ProcessFlowFeed', ProcessFlowFeed);

  // @NOTE: trendchart is really expensive, it uses chart.js & date-fns & google-palette and so it alone raises the
  //       bundle-size by 300kbs so by using an asynchronous component we can significantly cut down on avg bundle size
  //       for the slight cost of having to wait to render
  // we need a single word component name for this one, due to backwards compatibility reasons
  // eslint-disable-next-line vue/multi-word-component-names
  defineComponent(
    'Trends',
    defineAsyncComponent(() => import('@Shared/components/visuals/TrendChart.vue'))
  );

  defineComponent('FrontlineTopBanner', FrontlineTopBanner);
  defineComponent('FrontlinePrimaryMetric', FrontlinePrimaryMetric);
  defineComponent('FrontlineProgramBreakdown', FrontlineProgramBreakdown);
  defineComponent('SmartInsights', SmartInsights);

  defineComponent('SocialReviewRatings', SocialReviewRatings);
  defineComponent('SocialReviewFeed', SocialReviewFeed);
  defineComponent('SocialReviewReplies', SocialReviewReplies);
  defineComponent('SocialReviewsGenerated', SocialReviewsGenerated);

  defineComponent('Favourites', Favourites);
  defineComponent('ImprovedLikeForLikeReport', ImprovedLikeForLikeReport);

  defineComponent('OneValueTile', OneValueTile);
  defineComponent('DaysSinceLastResponse', OneValueTile);
  defineComponent('MetricBreakdownTile', MetricBreakdownTile);
  defineComponent('MeasureTile', MeasureTile);
  defineComponent('SlicedMetricBreakdownTiles', SlicedMetricBreakdownTiles);
  defineComponent('TopNValuesTile', TopNValuesTile);
  defineComponent('TopValueByMeasureTile', TopValueByMeasureTile);

  // we implement the skeletons through the base tiles so there is no need for specialised visuals
  defineComponent('SkeletonOneValueTile', OneValueTile);
  defineComponent('SkeletonDaysSinceLastResponse', OneValueTile);
  defineComponent('SkeletonMetricBreakdownTile', OneValueTile);
  defineComponent('SkeletonMeasureTile', MeasureTile);
  defineComponent('SkeletonSlicedMetricBreakdownTiles', SlicedMetricBreakdownTiles);
  defineComponent('SkeletonTopNValuesTile', TopNValuesTile);
  defineComponent('SkeletonTopValueByMeasureTile', TopValueByMeasureTile);

  defineComponent('SkeletonCardCarousel', SkeletonCardCarousel);
  defineComponent('SkeletonDriverGauges', SkeletonDriverGauges);
  defineComponent('SkeletonProgramDriverGauges', SkeletonProgramDriverGauges);
  defineComponent('SkeletonGaugeChart', SkeletonGaugeChart);
  defineComponent('SkeletonMeasureLeaderboard', SkeletonMeasureLeaderboard);
  defineComponent('SkeletonMeasureList', SkeletonMeasureList);
  defineComponent('SkeletonProcessFlowFeed', SkeletonProcessFlowFeed);
  defineComponent('SkeletonTrends', SkeletonTrendChart);

  defineComponent('SkeletonFrontlineTopBanner', SkeletonFrontlineTopBanner);
  defineComponent('SkeletonFrontlinePrimaryMetric', SkeletonFrontlinePrimaryMetric);
  defineComponent('SkeletonFrontlineProgramBreakdown', SkeletonFrontlineProgramBreakdown);
  defineComponent('SkeletonSmartInsights', SkeletonSmartInsights);

  defineComponent('SkeletonSocialReviewRatings', SkeletonSocialReviewRatings);
  defineComponent('SkeletonSocialReviewFeed', SocialReviewFeed);
  defineComponent('SkeletonSocialReviewReplies', SkeletonSocialReviewReplies);
  defineComponent('SkeletonSocialReviewsGenerated', SkeletonSocialReviewsGenerated);

  // we implement the skeletons through the base tiles so there is no need for specialised visuals
  defineComponent('SkeletonFavourites', Favourites);
  defineComponent('SkeletonImprovedLikeForLikeReport', ImprovedLikeForLikeReport);

  // @NOTE: BarChart is expensive — it pulls in chart.js, chartjs-plugin-annotation,
  //        chartjs-plugin-datalabels, and markdown-it, so we lazy-load it like TrendChart
  defineComponent(
    'BarChart',
    defineAsyncComponent(() => import('./BarChart.vue'))
  );
  defineComponent('NpsStackedBarWithLine', NpsStackedBarWithLine);

  // Use BarChart skeleton for NPS chart since it's similar
  defineComponent(
    'SkeletonNpsStackedBarWithLine',
    defineAsyncComponent(() => import('./BarChart.vue'))
  );

  defineComponent('ClosingTime', ClosingTime);
  defineComponent('CommonTopics', CommonTopics);
  defineComponent('DetractorFeedbackCount', DetractorFeedbackCount);
  defineComponent('FeedbackTopic', FeedbackTopic);
  defineComponent('ImprovementPoint', ImprovementPoint);
  defineComponent('LowHangingDriver', LowHangingDriver);
  defineComponent('MetricChanged', MetricChanged);
  defineComponent('OpenedFeedback', OpenedFeedback);
  defineComponent('PerformanceSummary', PerformanceSummary);
  defineComponent('TopBottomPerformers', TopBottomPerformers);
}
